<template>
  <div class="aggregation-grid">
    <div class="grid-scroll">
      <table class="grid">
        <thead>
          <tr class="grid-head">
            <th
              v-for="header in headers"
              :key="`grid-head-${header.value}`"
              class="grid-cell grid-head-cell"
              :class="{
                'grid-head-sorted': sortKey === header.value,
                'grid-cell-fixed-col': header.value === 'name',
                'grid-cell-fixed-corner': header.value === 'name',
              }"
              role="button"
              :aria-sort="ariaSort(header.value)"
              @click="toggleSort(header.value)"
            >
              <span class="grid-head-content">
                <span class="grid-head-text">{{ header.text }}</span>
                <!-- ソート中の列: ▲/▼ をはっきり表示 -->
                <v-icon
                  v-if="sortKey === header.value"
                  x-small
                  class="grid-sort-icon"
                >
                  {{ sortOrder === 'asc' ? icons.mdiArrowUp : icons.mdiArrowDown }}
                </v-icon>
                <!-- 未ソートの列: ホバー時のみ薄い昇順矢印を表示（CSS で opacity 制御） -->
                <v-icon
                  v-else
                  x-small
                  class="grid-sort-icon grid-sort-icon-hint"
                >
                  {{ icons.mdiArrowUp }}
                </v-icon>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, rowIdx) in sortedItems"
            :key="`grid-row-${item.contextType}-${item.id || rowIdx}`"
          >
            <td
              v-for="header in headers"
              :key="`grid-cell-${item.id || rowIdx}-${header.value}`"
              class="grid-cell"
              :class="cellClasses(item, header)"
              @click="onCellClick(item, header)"
            >
              <!-- 行見出し（ユーザー名など。編集不可） -->
              <template v-if="header.value === 'name'">
                {{ item.name }}
              </template>

              <!-- boolean セル: クリックでトグル（チェックボックス表示） -->
              <template v-else-if="isBooleanCell(item, header.value)">
                <v-icon
                  small
                  :color="effectiveValue(item, header.value) ? 'primary' : ''"
                >
                  {{ effectiveValue(item, header.value) ? icons.mdiCheckboxMarked : icons.mdiCheckboxBlankOutline }}
                </v-icon>
              </template>

              <!-- 編集中の number セル: セル内に直接数値入力欄を表示 -->
              <input
                v-else-if="isEditingCell(item, header.value) && isNumberCell(item, header.value)"
                :ref="editingInputRef"
                v-model="editingValue"
                v-format-number-on-blur
                class="grid-cell-input grid-cell-input-number"
                type="number"
                inputmode="numeric"
                @blur="commitEdit"
                @keydown.enter.prevent="commitEditAndBlur"
                @keydown.esc.prevent="cancelEdit"
              >

              <!-- 編集中の text セル: セル内に直接テキスト入力欄を表示 -->
              <input
                v-else-if="isEditingCell(item, header.value)"
                :ref="editingInputRef"
                v-model="editingValue"
                class="grid-cell-input"
                type="text"
                @blur="commitEdit"
                @keydown.enter.prevent="commitEditAndBlur"
                @keydown.esc.prevent="cancelEdit"
              >

              <!-- 通常表示の text / number セル -->
              <template v-else>
                {{ displayCellValue(item, header.value) }}
              </template>
            </td>
          </tr>

          <tr v-if="items.length === 0">
            <td
              :colspan="headers.length || 1"
              class="grid-cell grid-empty"
            >
              データがありません
            </td>
          </tr>
        </tbody>

        <!-- 合計行（数値列の合計。常時表示） -->
        <tfoot v-if="items.length > 0">
          <tr>
            <td
              v-for="header in headers"
              :key="`grid-sum-${header.value}`"
              class="grid-cell grid-sum-cell"
              :class="{
                'grid-cell-number': header.value !== 'name',
                'grid-cell-fixed-col': header.value === 'name',
              }"
            >
              {{ sumCellText(header) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  ref, computed, watch, onUnmounted, nextTick,
} from '@vue/composition-api'
import {
  mdiCheckboxMarked, mdiCheckboxBlankOutline, mdiArrowUp, mdiArrowDown,
} from '@mdi/js'
import { debounce } from 'lodash'
import { judgeType, interpretValue } from '@/utils/typeUtils'
import formatNumberOnBlur from '@/directives/formatNumberOnBlur'

// 保存リクエスト（emit save → API）のデバウンス間隔。
// 注意: デバウンスは「保存リクエスト」だけに効かせる。表示の楽観的更新は
// commitEdit 時に即座に反映する（debounce で遅らせない）。
const SAVE_DEBOUNCE_MS = 500

// 楽観的更新ストア（モジュールスコープ）。
// 集計データの再取得などで AggregationGrid が一旦アンマウント→再マウント
// されても入力値が失われないよう、コンポーネントの setup スコープ外に置く。
// キーは `aggregationId::contextType-contextId-businessRuleId` で集計表ごとに
// 衝突しない。Vue.observable でリアクティブにし、表示へ即時反映させる。
const optimisticOverrides = Vue.observable({ map: {} })

/**
 * 新クロージングページ専用の集計表グリッド。
 *
 * 既存 `eod-closing/components/AggregationTable.vue` および `useAggregationTable` の
 * ロジック（ヘッダー/行の組み立て、セル種別判定、未保存判定）を参考に、
 * Vuetify v-data-table に依存しないネイティブ table で再実装している。
 * セル編集は Excel のようなインライン編集（セル内に直接入力欄を表示）。
 * モック closing-page.html のスプレッドシート（全セル罫線の格子）に忠実な見た目とする。
 */
export default {
  name: 'AggregationGrid',
  directives: {
    formatNumberOnBlur,
  },
  props: {
    // 集計表（JSON:API 構造の attributes を持つオブジェクト）
    aggregation: {
      type: Object,
      default: () => ({
        attributes: {
          id: null,
          name: '',
          type: '',
          businessRules: { data: [] },
          contexts: { data: [] },
        },
      }),
    },
    // セル操作のロック（保存中・集計中など）
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // --- 集計表タイプ判定 -------------------------------------------------
    const isUserAggregation = computed(() => (
      /(UserAggregation|PayrollAggregation)$/.test(props.aggregation.attributes.type || '')
    ))

    // --- ヘッダー組み立て ------------------------------------------------
    // ビジネスルール列。ユーザー集計のときは先頭に「ユーザー」見出し列を足す。
    const headers = computed(() => {
      const businessRules = props.aggregation.attributes.businessRules?.data || []
      const ruleHeaders = businessRules.map(rule => ({
        text: rule.attributes.name,
        value: rule.id,
      }))

      if (isUserAggregation.value) {
        return [{ text: 'ユーザー', value: 'name' }, ...ruleHeaders]
      }

      return ruleHeaders
    })

    // --- 行（item）組み立て ---------------------------------------------
    // 1 context = 1 行。businessRuleId をキーに値・メタ情報を持たせる。
    const buildItem = context => {
      const {
        contextType, id: contextId, name: contextName, results,
      } = context.attributes

      const item = {
        contextType,
        id: contextId,
        name: contextName,
        values: {},
        meta: {
          businessRuleResultIds: {},
          editable: {},
          inputTypes: {},
          originalValues: {},
        },
      }

      const resultsData = results?.data || []
      resultsData.forEach(result => {
        const {
          businessRuleId, businessRuleResultId, value, editable,
        } = result.attributes

        // 配列・オブジェクト値は表セルに載せない（既存 AggregationTable と同じ扱い）
        if (Array.isArray(value)) return
        if (value !== null && typeof value === 'object') return

        const interpreted = interpretValue(value)
        item.values[businessRuleId] = interpreted
        item.meta.businessRuleResultIds[businessRuleId] = businessRuleResultId
        item.meta.editable[businessRuleId] = editable
        item.meta.inputTypes[businessRuleId] = judgeType(value)
        item.meta.originalValues[businessRuleId] = interpreted
      })

      return item
    }

    const items = computed(() => (
      (props.aggregation.attributes.contexts?.data || []).map(buildItem)
    ))

    // --- 列（ビジネスルール）単位の値型判定 -----------------------------
    // 共有の judgeType は値が空文字のとき Number('') === 0 のため 'number' を
    // 返してしまう。その結果、空セルを持つ text 列が number 列と誤判定され、
    // type="number" の input になり、文字列を入力できない（数値入力欄に
    // 弾かれ、v-format-number-on-blur で 0 にされる）バグが起きていた。
    //
    // 対策として、セル単体ではなく「列全体の実値」から型を決める。
    // 1 つでも非数値の文字列があれば text、boolean があれば boolean、
    // それ以外で数値があれば number、値が一切無ければ text（既定）。
    // 空値（'' / null / undefined）は型判定に寄与させない。

    // 単一値の型を 'empty' / 'boolean' / 'number' / 'text' に分類する。
    const classifyValue = value => {
      if (value === null || value === undefined || value === '') return 'empty'
      if (typeof value === 'boolean') return 'boolean'
      const str = `${value}`.trim()
      if (str === '') return 'empty'
      if (['true', 'false'].includes(str.toLowerCase())) return 'boolean'
      if (!Number.isNaN(Number(str))) return 'number'

      return 'text'
    }

    // businessRuleId -> 列の型（'boolean' | 'number' | 'text'）
    const columnTypes = computed(() => {
      const types = {}
      const contexts = props.aggregation.attributes.contexts?.data || []
      contexts.forEach(context => {
        const resultsData = context.attributes?.results?.data || []
        resultsData.forEach(result => {
          const attrs = result.attributes || {}
          const { businessRuleId, value } = attrs
          if (businessRuleId === null || businessRuleId === undefined) return
          if (Array.isArray(value)) return
          if (value !== null && typeof value === 'object') return

          const kind = classifyValue(value)
          if (kind === 'empty') return

          const current = types[businessRuleId]
          // 優先度: text > boolean > number（text を最優先で確定させる）
          if (kind === 'text') {
            types[businessRuleId] = 'text'
          } else if (kind === 'boolean') {
            if (current !== 'text') types[businessRuleId] = 'boolean'
          } else if (kind === 'number') {
            if (current !== 'text' && current !== 'boolean') types[businessRuleId] = 'number'
          }
        })
      })

      return types
    })

    // セルの型を返す。列全体で型が確定していればそれを、無ければ 'text'
    // （text input は値を強制変換しないため、未知の場合の安全側）。
    const cellType = businessRuleId => columnTypes.value[businessRuleId] || 'text'

    // --- 楽観的更新（保存値の即時反映）---------------------------------
    // セル単位の識別キー（contextType-contextId-businessRuleId）。
    const cellKey = (item, businessRuleId) => (
      `${item.contextType}-${item.id || 'null'}-${businessRuleId}`
    )

    // モジュールスコープの楽観的更新ストア用キー（集計表ごとに衝突しない）。
    const overrideKey = (item, businessRuleId) => (
      `${props.aggregation.attributes.id}::${cellKey(item, businessRuleId)}`
    )

    // 楽観的更新値を書き込む（commitEdit / boolean トグル時に即座に呼ぶ）。
    const setOverride = (item, businessRuleId, value) => {
      Vue.set(optimisticOverrides.map, overrideKey(item, businessRuleId), value)
    }

    // セルの実効値（楽観更新があればそれを優先、なければ集計データの値）。
    // items は computed 由来の派生オブジェクトで直接書き換えてもリアクティブに
    // 反映されないため、モジュールスコープのリアクティブストアで上書き表示する。
    const effectiveValue = (item, businessRuleId) => {
      const key = overrideKey(item, businessRuleId)
      if (Object.prototype.hasOwnProperty.call(optimisticOverrides.map, key)) {
        return optimisticOverrides.map[key]
      }

      return item.values[businessRuleId]
    }

    // --- 列ソート -------------------------------------------------------
    // 既存 AggregationTable.vue（v-data-table）のソート挙動に準拠。
    // 列ヘッダークリックで「昇順 → 降順 → ソート解除（元の順）」をトグルする。
    const sortKey = ref(null)
    const sortOrder = ref('asc')

    const toggleSort = key => {
      if (sortKey.value !== key) {
        // 別の列をクリック: その列の昇順ソート
        sortKey.value = key
        sortOrder.value = 'asc'

        return
      }
      if (sortOrder.value === 'asc') {
        // 昇順 → 降順
        sortOrder.value = 'desc'

        return
      }
      // 降順 → ソート解除（元の順）
      sortKey.value = null
      sortOrder.value = 'asc'
    }

    const ariaSort = key => {
      if (sortKey.value !== key) return 'none'

      return sortOrder.value === 'asc' ? 'ascending' : 'descending'
    }

    // ソート対象セルの生値を取り出す（name 列は行見出し、それ以外は
    // 楽観更新を反映した実効値）
    const sortRawValue = (item, key) => (
      key === 'name' ? item.name : effectiveValue(item, key)
    )

    // 2値を比較する。数値・真偽値・文字列を適切に扱い、空値は末尾に寄せる。
    const compareValues = (a, b) => {
      const aEmpty = a === null || a === undefined || a === ''
      const bEmpty = b === null || b === undefined || b === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1

      // boolean: false < true
      if (typeof a === 'boolean' || typeof b === 'boolean') {
        return (a ? 1 : 0) - (b ? 1 : 0)
      }

      // number: 両方が数値として解釈できれば数値比較
      const aNum = Number(a)
      const bNum = Number(b)
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum
      }

      // それ以外は文字列比較（日本語ロケール）
      return String(a).localeCompare(String(b), 'ja')
    }

    // 表示用の行リスト。ソート未指定なら元の順、指定時は安定ソートする。
    const sortedItems = computed(() => {
      if (!sortKey.value) return items.value

      const key = sortKey.value
      const dir = sortOrder.value === 'desc' ? -1 : 1

      // index を添えて安定ソート（同値は元の順を維持）
      return items.value
        .map((item, index) => ({ item, index }))
        .sort((x, y) => {
          const cmp = compareValues(sortRawValue(x.item, key), sortRawValue(y.item, key))
          if (cmp !== 0) return cmp * dir

          return x.index - y.index
        })
        .map(entry => entry.item)
    })

    // --- セル種別・状態判定 ---------------------------------------------
    // 型判定は列全体から決めた cellType を使う（空セルの誤判定を防ぐ）。
    // 第1引数 item は呼び出し側の互換のため受け取るが使用しない。
    const isBooleanCell = (item, businessRuleId) => (
      cellType(businessRuleId) === 'boolean'
    )
    const isNumberCell = (item, businessRuleId) => (
      cellType(businessRuleId) === 'number'
    )
    // 未保存セル: 値はあるが businessRuleResultId が無い（＝まだ保存されていない）
    const isUnsavedCell = (item, businessRuleId) => (
      !item.meta.businessRuleResultIds[businessRuleId]
      && item.values[businessRuleId] !== null
      && item.values[businessRuleId] !== undefined
    )
    const isEditableCell = (item, businessRuleId) => (
      !props.loading && !!item.meta.editable[businessRuleId]
    )

    // --- インラインセル編集 ---------------------------------------------
    // 編集中セルの識別キー（contextType-contextId-businessRuleId）と編集値。
    const editingCellKey = ref(null)
    const editingValue = ref('')
    let editingContext = null // { item, businessRuleId, isNumber, originalValue }

    const displayCellValue = (item, businessRuleId) => {
      const value = effectiveValue(item, businessRuleId)
      if (value === null || value === undefined) return ''
      if (isNumberCell(item, businessRuleId)) {
        return Number(value).toLocaleString('ja-JP')
      }

      return value
    }

    const isEditingCell = (item, businessRuleId) => (
      editingCellKey.value === cellKey(item, businessRuleId)
    )

    // 編集中 input への参照（フォーカス用）。:ref に関数を渡して取得する。
    // v-for 内の関数 ref はノード除去時に null で呼ばれるため、null では
    // 上書きしない（直前の編集 input 除去が新しい参照を消さないように）。
    const editingInputEl = ref(null)
    const editingInputRef = el => {
      if (el) editingInputEl.value = el
    }

    // 編集中 input に確実にフォーカスする。
    // 編集モードへ切り替えた直後は、nextTick だけだとブラウザのレイアウト/
    // ペイントが間に合わずワンクリックでフォーカスが当たらないことがあるため、
    // nextTick の後さらに requestAnimationFrame で 1 フレーム待ってから
    // focus() / select() する。
    const focusEditingInput = async () => {
      await nextTick()
      const apply = () => {
        const el = editingInputEl.value
        if (el && typeof el.focus === 'function') {
          el.focus()
          if (typeof el.select === 'function') el.select()
        }
      }
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(apply)
      } else {
        apply()
      }
    }

    // 保存ペイロードを組み立てて emit する（既存と同形）。
    const emitSave = (item, businessRuleId, resultValue) => {
      emit('save', {
        aggregationId: props.aggregation.attributes.id,
        contextId: item.id,
        contextType: item.contextType,
        businessRuleId,
        resultValue,
      })
    }

    // 保存リクエスト（emit save → API）のみデバウンスする。
    // 表示の楽観的更新（setOverride）は debounce の外で即座に行う。
    const debouncedSave = debounce((item, businessRuleId, resultValue) => {
      emitSave(item, businessRuleId, resultValue)
    }, SAVE_DEBOUNCE_MS)

    // 値を正規化（数値セルは Number 化、空は空文字のまま）して保存する。
    const saveCellValue = (item, businessRuleId, isNumber, rawValue) => {
      let resultValue = rawValue
      if (isNumber && resultValue !== null && resultValue !== undefined && resultValue !== '') {
        resultValue = Number(resultValue)
      }
      // 楽観的更新を即座に反映（表示はここで確定。debounce で遅らせない）。
      setOverride(item, businessRuleId, resultValue)
      // 保存リクエストだけデバウンスする。
      debouncedSave(item, businessRuleId, resultValue)
    }

    // 編集を確定（blur 時）。値が実際に変わったときのみ保存する。
    const commitEdit = () => {
      if (!editingContext) return
      const {
        item, businessRuleId, isNumber, originalValue,
      } = editingContext

      let nextValue = editingValue.value
      if (isNumber && nextValue !== '' && nextValue !== null && nextValue !== undefined) {
        nextValue = Number(nextValue)
      }

      // 変更がなければ保存しない
      const changed = String(nextValue ?? '') !== String(originalValue ?? '')
      if (changed) {
        saveCellValue(item, businessRuleId, isNumber, editingValue.value)
      }

      editingCellKey.value = null
      editingContext = null
    }

    // Enter: 確定して input から抜ける（blur が commitEdit を呼ぶ）
    const commitEditAndBlur = () => {
      if (editingInputEl.value) {
        editingInputEl.value.blur()
      } else {
        commitEdit()
      }
    }

    // Esc: 編集をキャンセルし、値を元に戻す
    const cancelEdit = () => {
      editingContext = null
      editingCellKey.value = null
    }

    // セルクリック時の入口処理。
    //  - name 列・編集不可セル: 何もしない
    //  - boolean セル: クリックでトグルし、変更があれば保存
    //  - text / number セル: そのセルをインライン編集モードにする
    const onCellClick = async (item, header) => {
      if (header.value === 'name') return
      const businessRuleId = header.value
      if (!isEditableCell(item, businessRuleId)) return

      // boolean セル: クリックでトグル
      if (isBooleanCell(item, businessRuleId)) {
        const current = !!effectiveValue(item, businessRuleId)
        const next = !current
        saveCellValue(item, businessRuleId, false, next)

        return
      }

      // 既に同じセルを編集中なら何もしない
      if (isEditingCell(item, businessRuleId)) return

      // 別セルを編集中だった場合、先に確定する
      if (editingContext) commitEdit()

      const isNumber = isNumberCell(item, businessRuleId)
      const originalValue = effectiveValue(item, businessRuleId)
      editingContext = {
        item, businessRuleId, isNumber, originalValue,
      }
      editingValue.value = originalValue === null || originalValue === undefined
        ? ''
        : String(originalValue)
      // 直前の編集 input の参照をクリアし、新しい input の関数 ref で
      // 確実に取り直せるようにする。
      editingInputEl.value = null
      editingCellKey.value = cellKey(item, businessRuleId)

      // input が描画されたらフォーカスして全選択する（ワンクリック編集）
      await focusEditingInput()
    }

    const cellClasses = (item, header) => {
      if (header.value === 'name') {
        // name 列はボディセルとして左端固定する
        return ['grid-row-head', 'grid-cell-fixed-col']
      }

      return {
        'grid-cell-editable': isEditableCell(item, header.value),
        'grid-cell-unsaved': isUnsavedCell(item, header.value),
        'grid-cell-number': isNumberCell(item, header.value),
        'grid-cell-editing': isEditingCell(item, header.value),
      }
    }

    // 集計表が切り替わったらソート状態・編集状態をリセットする。
    // 楽観的更新ストア（optimisticOverrides）はモジュールスコープで集計表ごとに
    // キー分離しているため、ここではクリアしない（再取得や再マウントでも
    // 入力値を保持し続ける。サーバ値が追いついた分は下記 watch で掃除する）。
    watch(() => props.aggregation.attributes.id, () => {
      sortKey.value = null
      sortOrder.value = 'asc'
      editingCellKey.value = null
      editingContext = null
    })

    // 集計データが更新され、サーバ値が楽観的更新値に追いついたら、その
    // override は不要になるため掃除する（サーバの新しい値で正しく更新
    // されるべきセルを、古い override がいつまでも隠さないようにする）。
    watch(items, newItems => {
      newItems.forEach(item => {
        Object.keys(item.values).forEach(brId => {
          const key = overrideKey(item, brId)
          if (!Object.prototype.hasOwnProperty.call(optimisticOverrides.map, key)) return
          const serverValue = item.values[brId]
          const overrideValue = optimisticOverrides.map[key]
          // サーバ値と override が一致したら override を破棄する
          if (String(serverValue ?? '') === String(overrideValue ?? '')) {
            Vue.delete(optimisticOverrides.map, key)
          }
        })
      })
    })

    onUnmounted(() => {
      // 保留中のデバウンス保存があれば破棄する
      debouncedSave.cancel()
    })

    // --- 合計行 ---------------------------------------------------------
    // 既存 AggregationTable.vue の accRow を参考に、各数値列の合計を算出する。
    // 行見出し列（name）は「合計」ラベル、数値化できない列は空にする。
    const accRow = computed(() => {
      const sums = {}
      headers.value.forEach(header => {
        if (header.value === 'name') {
          sums[header.value] = '合計'

          return
        }

        let hasNumeric = false
        const total = items.value.reduce((acc, item) => {
          const val = effectiveValue(item, header.value)
          if (val === null || val === undefined || val === '') return acc
          // boolean 値は合計対象外（Number(false)=0/Number(true)=1 で
          // 誤って数値扱いされ、boolean 列に意味のない合計が出るのを防ぐ）
          if (typeof val === 'boolean') return acc
          const numVal = Number(val)
          if (Number.isNaN(numVal)) return acc
          hasNumeric = true

          return acc + numVal
        }, 0)
        sums[header.value] = hasNumeric ? total : ''
      })

      return sums
    })

    const sumCellText = header => {
      const val = accRow.value[header.value]
      if (val === '' || val === null || val === undefined) return val
      if (header.value === 'name') return val

      return Number(val).toLocaleString('ja-JP')
    }

    // クリップボードコピー用のTSV文字列を組み立てる（ヘッダー＋データ行＋合計行）。
    // データ行は現在の表示順（ソート適用後）に従う。
    // 親（DailyAggregationStep）の ⋮ メニューから template ref 経由で呼ばれる。
    const buildClipboardText = () => {
      const headerRow = headers.value.map(h => h.text).join('\t')
      const dataRows = sortedItems.value.map(item => (
        headers.value.map(h => {
          if (h.value === 'name') return item.name
          const val = effectiveValue(item, h.value)

          return val === null || val === undefined ? '' : val
        }).join('\t')
      ))
      const sumRow = headers.value.map(h => {
        const val = accRow.value[h.value]

        return val === null || val === undefined ? '' : val
      }).join('\t')

      return [headerRow, ...dataRows, sumRow].join('\n')
    }

    return {
      headers,
      items,
      sortedItems,
      sortKey,
      sortOrder,
      toggleSort,
      ariaSort,
      isBooleanCell,
      isNumberCell,
      isEditableCell,
      effectiveValue,
      displayCellValue,
      cellClasses,
      sumCellText,
      buildClipboardText,
      onCellClick,
      isEditingCell,
      editingValue,
      editingInputRef,
      commitEdit,
      commitEditAndBlur,
      cancelEdit,
      icons: {
        mdiCheckboxMarked,
        mdiCheckboxBlankOutline,
        mdiArrowUp,
        mdiArrowDown,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.aggregation-grid {
  position: relative;
}

// 横スクロール用コンテナ。
//
// max-height は付けない。max-height を付けて縦スクロールコンテナ化すると、
// 縦スクロールバー領域分のスペースが右に出てしまうため（ユーザー DevTools
// で確認済みの右余白の原因）。よって縦スクロールはページ全体側で行う。
//
// 横スクロール（および name 列の sticky 固定）のために overflow-x は auto。
// overflow-y は visible を指定。CSS 仕様上 overflow-x が auto のとき
// overflow-y も auto に算出されるが、max-height が無く中身の高さ＝コンテナ
// 高さのため、実際に縦スクロールは発生せず縦スクロールバーも出ない。
.grid-scroll {
  overflow-x: auto;
  overflow-y: visible;

  // 横スクロール機能は残しつつ、横スクロールバーの表示だけ消す。
  &::-webkit-scrollbar {
    height: 0;
  }
}

// スプレッドシート: 全セル罫線の格子。
//
// width: 100% でテーブルを常にスクロールコンテナ幅いっぱいにする。
//  - 列が少なく内容が狭いとき → ブラウザが列を広げて埋め、右端に余白が出ない。
//  - 列が多く内容が広いとき → セルが white-space: nowrap で縮まないため
//    テーブルは 100% を超えて広がり、横スクロールで見られる。
table.grid {
  border-collapse: collapse;
  width: 100%;
  table-layout: auto;
  font-size: 13px;
}

.grid-cell {
  border: 1px solid rgba(0, 0, 0, .16);
  padding: 7px 16px;
  white-space: nowrap;
  text-align: right;
  min-width: 120px;
  // セル幅をヘッダー/データ/合計で確実に揃えるため box-sizing を明示
  box-sizing: border-box;
}

// ダークテーマ（Materio ダーク）で視認できる罫線色
.theme--dark .grid-cell {
  border-color: rgba(231, 227, 252, .16);
}

.grid-head-cell {
  text-align: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  // sticky 固定時に透けないよう不透明な見出し背景にする
  background: #f4f5fa;

  &:hover {
    background: #e7e8f0;
  }
}

.theme--dark .grid-head-cell {
  background: #3a3552;

  &:hover {
    background: #443e60;
  }
}

// ソート中の列ヘッダーを強調（sticky で透けないよう不透明色）
.grid-head-sorted,
.grid-head-sorted:hover {
  background: #e4d8fc;
}

.theme--dark .grid-head-sorted,
.theme--dark .grid-head-sorted:hover {
  background: #4a3d6b;
}

// ヘッダーのラベルとソートアイコンの並び
.grid-head-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.grid-sort-icon {
  color: var(--v-primary-base, #9155fd) !important;
}

// 未ソート列のソートヒント矢印: 通常時は非表示、ヘッダーホバー時のみ薄く表示
.grid-sort-icon-hint {
  opacity: 0;
  transition: opacity .15s ease;
}

.grid-head-cell:hover .grid-sort-icon-hint {
  opacity: .5;
}

// 行見出し（ユーザー名など）は左寄せ・固定的な装い。
// 左端固定で透けないよう不透明な背景にする。
// 幅は .grid-cell-fixed-col で固定するため、長い名前は省略表示にする。
.grid-row-head {
  text-align: left;
  font-weight: 600;
  background: #f4f5fa;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .grid-row-head {
  background: #3a3552;
}

// 合計行。name 列は左端固定されるため、半透明だと透ける。
// 不透明な合計行背景にする。
.grid-sum-cell {
  font-weight: 700;
  background: #eceef5;
}

.theme--dark .grid-sum-cell {
  background: #383350;
}

// ===== ウィンドウ枠固定（Excel のような sticky ヘッダー / 列） =====
// sticky セルはスクロール時に下の内容が透けないよう、背景を不透明にする
// （背景色は上の .grid-head-cell / .grid-row-head / .grid-sum-cell 各規則で
//  不透明色を指定済み。ここでは位置と重なり順のみ定義する）。
// 既存 eod-closing/AggregationTable.vue の fixed-cell（position: sticky）を参考。
//
// z-index の重なり順（高い順）:
//   交差セル(4) > ヘッダー行(3) > name 列(2) > 通常セル(既定)

// ヘッダー行: 縦スクロールで上端に固定
.grid-head-cell {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 3;
}

// name 列: 横スクロールで左端に固定（ヘッダー/ボディ/合計行 共通）。
//
// name 列にだけ position: sticky を当てると、ヘッダー <th> と本文 <td> が
// それぞれ別の合成レイヤーになり、border-collapse 下で列幅の算出が
// 食い違うことがある（ヘッダーと本文で列がズレ、右端に幅のズレ分の
// 隙間や見かけ上の余剰列が出る原因）。
// 幅を固定値で明示し、ヘッダー/本文/合計行で必ず同じ幅になるようにする。
.grid-cell-fixed-col {
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 2;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
}

// 左上の交差セル（ヘッダー行 × name 列）: 縦横とも固定し最前面
.grid-cell-fixed-corner {
  z-index: 4;
}

.grid-cell-number {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

// 編集可能セルはホバーで枠ハイライト
.grid-cell-editable {
  cursor: pointer;

  &:hover {
    outline: 2px solid var(--v-primary-base, #9155fd);
    outline-offset: -2px;
  }
}

// 編集中セル: 枠を常時ハイライト
.grid-cell-editing {
  outline: 2px solid var(--v-primary-base, #9155fd);
  outline-offset: -2px;
  // 編集中はセル内の input をぴったり収めるため padding を詰める
  padding: 0;
}

// 未保存セルは下線で示す
.grid-cell-unsaved {
  border-bottom: 2px solid var(--v-primary-base, #9155fd);
}

// インライン編集用の入力欄。セルにぴったり収まるよう装飾を消す。
.grid-cell-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 7px 16px;
  box-sizing: border-box;
  text-align: right;
}

.grid-cell-input-number {
  text-align: right;
  font-variant-numeric: tabular-nums;

  // number input のスピンボタン（上下矢印）を非表示にする
  -moz-appearance: textfield; // Firefox

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.grid-empty {
  text-align: center;
  color: rgba(0, 0, 0, .5);
  padding: 24px 16px;
}

.theme--dark .grid-empty {
  color: rgba(231, 227, 252, .5);
}
</style>
